// 📄 services/iflowService.ts
// Repositorio de iFlows (SAP BTP · Integration Suite) — ver
// docs/plans/plan-repositorio-iflows-v1.md. El repositorio es de la
// ORGANIZACIÓN activa (personal o de equipo).
//
// - Lectura de la lista: directo de Firestore
//   (organizations/{orgId}/iflows), permitido por reglas a owner/admin/miembro.
// - Todo lo demás pasa por onehubApi (/v1/iflows): el navegador solo sube
//   los archivos a SU staging (staging/iflows/{uid}/…) y el backend
//   los valida, los guarda en una carpeta que ninguna regla expone y borra
//   el staging.
import { getAuth } from 'firebase/auth';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';

export type EstadoIflow = 'borrador' | 'probado' | 'productivo';

export const ESTADOS_IFLOW: { value: EstadoIflow; label: string }[] = [
  { value: 'borrador', label: 'Borrador' },
  { value: 'probado', label: 'Probado' },
  { value: 'productivo', label: 'En producción' }
];

export interface AdaptadorIflow {
  nombre: string;
  tipo: string;
  direccion: string;
  transporte: string;
  mensaje: string;
  autenticacion: string;
  conectividad: string;
}

export interface ParametroIflow {
  nombre: string;
  conValor: boolean;
}

export interface DocumentoIflow {
  id: string;
  nombre: string;
  tipo: string;
  tamano: number;
  subidoPor: string;
  fecha?: any;
}

/** Quién marcó un estado, cuándo y sobre qué revisión (registro, no permiso). */
export interface RegistroEstado {
  estado: EstadoIflow;
  uid: string;
  nombre: string;
  revision: number;
  fecha?: any;
  // "nueva-revision": vuelta automática a borrador al subir un .zip distinto.
  motivo?: string;
}

/** Dónde quedó publicado un iFlow personal (solo en el iFlow de origen). */
export interface PublicacionIflow {
  orgId: string;
  iflowId: string;
  revisionPublicada: number;
  fecha?: any;
}

export interface Iflow {
  id: string;
  orgId: string;
  titulo: string;
  descripcion: string;
  sistemaOrigen: string;
  sistemaDestino: string;
  solucion: string;
  etiquetas: string[];
  estado: EstadoIflow;
  proyectoId: string;
  notas: string;
  idTecnico: string;
  nombreTecnico: string;
  versionIflow: string;
  adaptadores: AdaptadorIflow[];
  pasos: Record<string, number>;
  parametros: ParametroIflow[];
  conteos: { scripts: number; mapeos: number; esquemas: number };
  tieneValoresFijos: boolean;
  autorUid: string;
  autorNombre: string;
  versionActual: number;
  tamano: number;
  documentos: DocumentoIflow[];
  publicadoDesdePersonal?: boolean;
  aprobacion?: RegistroEstado | null;
  historialEstado?: RegistroEstado[];
  publicaciones?: PublicacionIflow[];
  creadoEn?: any;
  actualizadoEn?: any;
}

/** Valor fijo de un adaptador (host, alias, location), YA enmascarado por el backend. */
export interface ValorFijo {
  adaptador: string;
  campo: string;
  valor: string;
}

export interface AnalisisIflow {
  idTecnico: string;
  nombreTecnico: string;
  versionIflow: string;
  descripcionTenant: string;
  adaptadores: AdaptadorIflow[];
  pasos: Record<string, number>;
  parametros: ParametroIflow[];
  conteos: { scripts: number; mapeos: number; esquemas: number };
  valoresFijos: ValorFijo[];
  parametrosConValor: { nombre: string; valor: string }[];
  tamano: number;
}

export interface DatosIflow {
  titulo: string;
  descripcion: string;
  sistemaOrigen?: string;
  sistemaDestino?: string;
  solucion?: string;
  etiquetas?: string[];
  estado?: EstadoIflow;
  proyectoId?: string;
  notas?: string;
}

// Límites (espejo de functions/services/iflowZip.js e iflowDocumento.js): acá
// solo sirven para avisar antes de subir; el backend es quien decide.
export const LIMITE_ZIP_BYTES = 10 * 1024 * 1024;
export const LIMITE_DOCUMENTO_BYTES = 20 * 1024 * 1024;
export const MAX_DOCUMENTOS = 5;
export const EXTENSIONES_DOCUMENTO = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'md', 'txt', 'png', 'jpg', 'jpeg'];
export const ACCEPT_DOCUMENTO = EXTENSIONES_DOCUMENTO.map((e) => `.${e}`).join(',');

export const extensionDe = (nombre: string): string => (/\.([A-Za-z0-9]{1,5})$/.exec(nombre)?.[1] || '').toLowerCase();

/** Error de /v1/iflows con su código (ej. FIXED_VALUES_CONFIRMATION_REQUIRED) y extras. */
export class IflowApiError extends Error {
  status: number;
  code: string;
  extra: Record<string, any>;

  constructor(status: number, code: string, message: string, extra: Record<string, any> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.extra = extra;
  }
}

const getToken = async (): Promise<string> => {
  const user = getAuth().currentUser;
  if (!user) throw new Error('No se detectó una sesión activa. Por favor, reingresa al sistema.');
  return user.getIdToken();
};

const pedir = async (metodo: string, ruta: string, body?: object): Promise<Response> => {
  const token = await getToken();
  const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/iflows${ruta}`, {
    method: metodo,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  if (!res.ok) {
    const { code = 'ERROR', error, ...extra } = await res.json().catch(() => ({}));
    throw new IflowApiError(res.status, code, error || `Error ${res.status} al comunicarse con el servidor.`, extra);
  }
  return res;
};

const pedirJson = async <T,>(metodo: string, ruta: string, body?: object): Promise<T> => (await pedir(metodo, ruta, body)).json();

// ── Lectura ───────────────────────────────────────────────────────────────

export const listarIflows = async (orgId: string): Promise<Iflow[]> => {
  const snap = await getDocs(query(collection(db, 'organizations', orgId, 'iflows'), orderBy('actualizadoEn', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Iflow, 'id'>) }));
};

// ── Archivos ──────────────────────────────────────────────────────────────

/** Sube un archivo al staging PROPIO del usuario y devuelve la ruta para el backend. */
export const subirAStaging = async (file: File): Promise<string> => {
  const uid = getAuth().currentUser?.uid;
  if (!uid) throw new Error('No se detectó una sesión activa. Por favor, reingresa al sistema.');
  const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const ruta = `staging/iflows/${uid}/${id}.${extensionDe(file.name)}`;
  await uploadBytes(ref(storage, ruta), file, { contentType: file.type || 'application/octet-stream' });
  return ruta;
};

const guardarComoArchivo = async (res: Response, nombrePorDefecto: string) => {
  const disposicion = res.headers.get('content-disposition') || '';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposicion)?.[1];
  const nombre = utf8 ? decodeURIComponent(utf8) : /filename="([^"]+)"/i.exec(disposicion)?.[1] || nombrePorDefecto;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ── API ───────────────────────────────────────────────────────────────────

export const analizarIflow = (orgId: string, stagingPath: string) =>
  pedirJson<AnalisisIflow>('POST', '/analyze', { orgId, stagingPath });

export const crearIflow = (params: {
  orgId: string;
  stagingPath: string;
  data: DatosIflow;
  documents?: { stagingPath: string; name: string }[];
  stripParameterValues: boolean;
  confirmFixedValues: boolean;
}) => pedirJson<{ iflowId: string; version: number; valoresVaciados: boolean; documentos: number }>('POST', '', params);

export const descargarIflow = async (orgId: string, iflowId: string, version?: number) =>
  guardarComoArchivo(await pedir('GET', `/${orgId}/${iflowId}/download${version ? `?version=${version}` : ''}`), 'iflow.zip');

// ── Diagrama (visor, docs/plans/plan-visor-diagrama-iflows.md) ────────────

export type TipoNodoDiagrama = 'pool' | 'participante' | 'paso' | 'inicio' | 'fin' | 'evento' | 'gateway' | 'subproceso' | 'otro';

export interface NodoDiagrama {
  id: string;
  tipo: TipoNodoDiagrama;
  // activityType del paso/gateway, emisor|receptor del participante, o la definición del evento (message, timer, error…).
  subtipo: string;
  // cname de la variante de SAP (GroovyScript, Enricher, XSLTMapping…).
  variante: string;
  nombre: string;
  x: number;
  y: number;
  ancho: number;
  alto: number;
  padre: string;
  // Solo pasos con algo útil que mostrar (script, mapping, splitter, Content Modifier).
  detalle: DetallePaso | null;
}

/** Fila de la tabla de un Content Modifier (header, propiedad o body). */
export interface FilaDetallePaso {
  grupo: string;
  accion: string;
  nombre: string;
  tipo: string;
  valor: string;
  // Valor constante: real solo con `valoresVisibles`; si no, ya viene enmascarado.
  protegido: boolean;
  // Solo para el dueño: clave de ValorEditable, para editarlo en línea.
  clave: string | null;
}

export interface DetallePaso {
  campos: { etiqueta: string; valor: string }[];
  filas: FilaDetallePaso[];
}

export interface ValorAdaptador {
  campo: string;
  // Real solo si el diagrama vino con `valoresVisibles`; si no, enmascarado por el backend.
  valor: string;
  parametrizado: boolean;
  // Solo para el dueño (y si no es parametrizado): clave de ValorEditable, para editarlo en línea.
  clave: string | null;
}

export interface AristaDiagrama {
  id: string;
  tipo: 'secuencia' | 'mensaje';
  nombre: string;
  desde: string;
  hasta: string;
  puntos: [number, number][];
  adaptador: (Omit<AdaptadorIflow, 'nombre'> & { valores: ValorAdaptador[] }) | null;
  // Rutas de un router.
  condicion: string;
  porDefecto: boolean;
}

export interface DiagramaIflow {
  version: number;
  limites: { x: number; y: number; ancho: number; alto: number };
  nodos: NodoDiagrama[];
  aristas: AristaDiagrama[];
  valoresVisibles: boolean;
}

export const obtenerDiagrama = (orgId: string, iflowId: string, version?: number) =>
  pedirJson<DiagramaIflow>('GET', `/${orgId}/${iflowId}/diagram${version ? `?version=${version}` : ''}`);

// ── Adaptar valores (E3, plan-visor-diagrama-iflows.md) ─────────────────

/** Un valor editable del iFlow, con su valor REAL (solo llega al dueño). */
export interface ValorEditable {
  // adaptador:<idMessageFlow>:<campo> · cm:<idPaso>:<Header|Propiedad>:<n> · cm:<idPaso>:Body · param:<nombre>
  clave: string;
  tipo: 'adaptador' | 'contentModifier' | 'parametro';
  // Sistema y tipo de adaptador, nombre del paso, o "Parámetros externalizados".
  grupo: string;
  campo: string;
  nombre: string;
  valor: string;
  // Armado en ejecución (${...}): no es un host fijo.
  dinamico: boolean;
}

export const obtenerValores = (orgId: string, iflowId: string) =>
  pedirJson<{ version: number; valores: ValorEditable[] }>('GET', `/${orgId}/${iflowId}/values`);

/** `rename`: nombre y/o ID nuevos (para importar en el mismo tenant); solo al descargar. */
export const descargarAdaptado = async (orgId: string, iflowId: string, version: number, changes: Record<string, string>, rename?: { name?: string; id?: string }) =>
  guardarComoArchivo(await pedir('POST', `/${orgId}/${iflowId}/adapt`, { version, changes, target: 'download', rename }), 'iflow_adaptado.zip');

// Espejo de ID_IFLOW_VALIDO (functions/services/iflowZip.js); el backend es quien decide.
export const ID_IFLOW_VALIDO = /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,99}$/;

export const guardarAdaptadoComoVersion = (orgId: string, iflowId: string, version: number, changes: Record<string, string>, note: string) =>
  pedirJson<{ iflowId: string; version: number; cambiados: number }>('POST', `/${orgId}/${iflowId}/adapt`, { version, changes, target: 'version', note });

export const descargarSinValores = async (orgId: string, iflowId: string) =>
  guardarComoArchivo(await pedir('GET', `/${orgId}/${iflowId}/download-clean`), 'iflow_sin_valores.zip');

export const descargarDocumentoIflow = async (orgId: string, iflowId: string, documentoId: string) =>
  guardarComoArchivo(await pedir('GET', `/${orgId}/${iflowId}/documents/${documentoId}/download`), 'documento');

export interface VersionIflow {
  numero: number;
  versionIflow: string;
  tamano: number;
  subidoPor: string;
  nota: string;
  fecha?: any;
}

export const listarVersiones = async (orgId: string, iflowId: string): Promise<VersionIflow[]> => {
  const snap = await getDocs(query(collection(db, 'organizations', orgId, 'iflows', iflowId, 'versiones'), orderBy('numero', 'desc')));
  return snap.docs.map((d) => d.data() as VersionIflow);
};

export const subirNuevaVersion = (orgId: string, iflowId: string, params: {
  stagingPath: string;
  note?: string;
  stripParameterValues: boolean;
  confirmFixedValues: boolean;
}) => pedirJson<{ iflowId: string; version: number; valoresVaciados: boolean }>('POST', `/${orgId}/${iflowId}/versions`, params);

export const editarIflow = (orgId: string, iflowId: string, data: Partial<DatosIflow>) =>
  pedirJson<{ iflowId: string }>('PATCH', `/${orgId}/${iflowId}`, { data });

export const borrarIflow = (orgId: string, iflowId: string) =>
  pedirJson<{ iflowId: string }>('DELETE', `/${orgId}/${iflowId}`);

export const publicarIflow = (orgId: string, iflowId: string, targetOrgId: string) =>
  pedirJson<{ orgId: string; iflowId: string; version: number; actualizado: boolean; soloDocumentos?: boolean; documentosAgregados?: number }>(
    'POST', `/${orgId}/${iflowId}/publish`, { targetOrgId }
  );

/** Sube los archivos al staging y los adjunta al iFlow. */
export const agregarDocumentosIflow = async (orgId: string, iflowId: string, files: File[]) => {
  const documents = await Promise.all(files.map(async (f) => ({ stagingPath: await subirAStaging(f), name: f.name })));
  return pedirJson<{ iflowId: string; documentos: { id: string; nombre: string }[] }>('POST', `/${orgId}/${iflowId}/documents`, { documents });
};

export const borrarDocumentoIflow = (orgId: string, iflowId: string, documentoId: string) =>
  pedirJson<{ iflowId: string; documentoId: string }>('DELETE', `/${orgId}/${iflowId}/documents/${documentoId}`);

/** Error de validación local de documentos (tipo, tamaño, cantidad) o null si están bien. */
export const validarDocumentosLocal = (actuales: number, nuevos: File[]): string | null => {
  const invalido = nuevos.find((f) => !EXTENSIONES_DOCUMENTO.includes(extensionDe(f.name)));
  if (invalido) return `"${invalido.name}": tipo no permitido (PDF, Word, Excel, PowerPoint, Markdown, TXT, PNG o JPG).`;
  const grande = nuevos.find((f) => f.size > LIMITE_DOCUMENTO_BYTES);
  if (grande) return `"${grande.name}" supera los 20 MB.`;
  if (actuales + nuevos.length > MAX_DOCUMENTOS) return `Máximo ${MAX_DOCUMENTOS} documentos por iFlow.`;
  return null;
};

export const tamanoLegible = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
